import { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PageLayout from "../../Components/UI/PageLayout.js";
import LoadingSpinner from "../../Components/UI/LoadingSpinner.js";
import PageError from "../../Components/UI/PageError.js";
import RegisterPlayerButton from "../../Components/Player/RegisterPlayerButton";
import RegisterManagerButton from "../../Components/Manager/RegisterManagerButton";
import { GET_CURRENT_USER, UPDATE_USER } from "../../GraphQL/Users";
import { GET_CURRENT_PLAYER } from "../../GraphQL/Players";
import { GET_CURRENT_MANAGER } from "../../GraphQL/Managers";
import { GET_APPEARANCES_BY_PLAYER_ID } from "../../GraphQL/Appearances";
import transparentLogo from "../../transparent-logo.png";

const AccountDetailsCard = ({ user }) => {
  const [editing, setEditing] = useState(false);
  const [editUsername, setEditUsername] = useState(user.username || "");
  const [updateUser] = useMutation(UPDATE_USER);

  const handleSave = () => {
    updateUser({
      variables: {
        id: user.id,
        discordId: user.discordId || "",
        discriminator: user.discriminator || 0,
        username: editUsername,
        email: user.email || "",
        icon: user.icon || "",
        originId: user.originId || "",
      },
    });
    setEditing(false);
  };

  const handleCancel = () => {
    setEditUsername(user.username || "");
    setEditing(false);
  };

  return (
    <Card sx={{ height: "100%", backgroundColor: "#2a3447" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          {user.icon ? (
            <img
              src={user.icon}
              alt="Avatar"
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                marginRight: 12,
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = transparentLogo;
              }}
            />
          ) : (
            <AccountCircleOutlinedIcon
              sx={{ fontSize: 48, color: "primary.main", mr: 1.5 }}
            />
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Account Details
          </Typography>
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Username
          </Typography>
          {editing ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <TextField
                size="small"
                value={editUsername}
                onChange={(e) => setEditUsername(e.target.value)}
                autoFocus
              />
              <IconButton size="small" onClick={handleSave} color="primary" aria-label="Save">
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={handleCancel} aria-label="Cancel">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body1">{user.username}</Typography>
              <IconButton
                size="small"
                onClick={() => setEditing(true)}
                aria-label="Edit username"
                sx={{ opacity: 0.5 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Email
          </Typography>
          <Typography variant="body1">{user.email || "—"}</Typography>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Discord ID
          </Typography>
          <Typography variant="body1">{user.discordId || "—"}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const ProfilePage = () => {
  const user = useQuery(GET_CURRENT_USER);
  const player = useQuery(GET_CURRENT_PLAYER);
  const manager = useQuery(GET_CURRENT_MANAGER);
  const appearances = useQuery(GET_APPEARANCES_BY_PLAYER_ID, {
    variables: { id: player.data?.currentPlayer?.id },
    skip: !player.data?.currentPlayer,
  });

  const queryError = user.error || player.error || manager.error;
  if (queryError) {
    return (
      <PageError
        message={queryError.message}
        onRetry={() => {
          user.refetch();
          player.refetch();
          manager.refetch();
        }}
      />
    );
  }

  if (user.loading || player.loading || manager.loading)
    return <LoadingSpinner />;

  const currentPlayer = player.data.currentPlayer;
  const currentManager = manager.data.currentManager;
  const playerAppearances = appearances.data?.appearancesByPlayerId ?? [];
  const totalGoals = playerAppearances.reduce((acc, a) => acc + a.goals, 0);
  const totalAssists = playerAppearances.reduce((acc, a) => acc + a.assists, 0);

  return (
    <PageLayout
      title="Profile"
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Profile" }]}
    >
      <Grid container spacing={3} alignItems="stretch">
        {/* Account Details */}
        <Grid item xs={12} md={4}>
          <AccountDetailsCard user={user.data.currentUser} />
        </Grid>

        {/* Player */}
        <Grid item xs={12} md={4}>
          {currentPlayer ? (
            <Card sx={{ height: "100%", backgroundColor: "#2a3447" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <SportsSoccerIcon
                    sx={{ fontSize: 48, color: "primary.main", mr: 1.5 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Player
                  </Typography>
                </Box>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {currentPlayer.name}
                </Typography>
                {currentPlayer.team && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Team:{" "}
                    <Link
                      to={`/team/${currentPlayer.team.id}`}
                      style={{ color: "inherit", textDecoration: "underline" }}
                    >
                      {currentPlayer.team.name}
                    </Link>
                  </Typography>
                )}

                <Grid container spacing={1} sx={{ mb: 3 }}>
                  {[
                    { value: playerAppearances.length, label: "Apps" },
                    { value: totalGoals, label: "Goals" },
                    { value: totalAssists, label: "Assists" },
                  ].map(({ value, label }) => (
                    <Grid item xs={4} key={label}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h5"
                          sx={{ color: "primary.main", fontWeight: 700 }}
                        >
                          {value}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Button
                  data-testid="playerButton"
                  component={Link}
                  to={`/player/${currentPlayer.id}`}
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  View Player Page
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ height: "100%", backgroundColor: "#2a3447" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <SportsSoccerIcon
                    sx={{ fontSize: 48, color: "text.disabled", mr: 1.5 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Player
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You are not registered as a player yet.
                </Typography>
                <RegisterPlayerButton />
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Manager */}
        <Grid item xs={12} md={4}>
          {currentManager ? (
            <Card sx={{ height: "100%", backgroundColor: "#2a3447" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <ManageAccountsIcon
                    sx={{ fontSize: 48, color: "primary.main", mr: 1.5 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Manager
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You are registered as a manager.
                </Typography>
                <Button
                  data-testid="viewManagerPageButton"
                  component={Link}
                  to={`/manager/${currentManager.id}`}
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  View Manager Page
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ height: "100%", backgroundColor: "#2a3447" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <ManageAccountsIcon
                    sx={{ fontSize: 48, color: "text.disabled", mr: 1.5 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Manager
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You are not registered as a manager yet.
                </Typography>
                <RegisterManagerButton />
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </PageLayout>
  );
};

export default ProfilePage;
